import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ParametresEntrepriseApi from '../api/ParametresEntrepriseApi';
import { buildAssetUrl } from '../utils/imageUrl';
import { applyBrowserFavicon } from '../utils/applyBrowserFavicon';
import { applyCssVariables } from '../theme';

import moment from 'moment'
const EntrepriseSettingsContext = createContext({
  settings: null,
  loading: true,
  refreshSettings: async () => {},
  getLogoUrl: () => null,
  getMinimalLogoUrl: () => null,
  getFaviconUrl: () => null,
  getFooterText: () => `TREND ©${moment().year()} Powered by WanderTeck`,
});

export const EntrepriseSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await ParametresEntrepriseApi.getParametres();
      setSettings(data);
      applyCssVariables(data);
    } catch (error) {
      console.error('Failed to load entreprise settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    applyCssVariables(null);
    refreshSettings();
  }, [refreshSettings]);

  const getLogoUrl = useCallback(
    (collapsed = false) => {
      const path = collapsed
        ? settings?.logo_minimal_url || settings?.logo_url
        : settings?.logo_url;
      return path ? buildAssetUrl(path) : null;
    },
    [settings]
  );

  const getMinimalLogoUrl = useCallback(() => {
    const path = settings?.logo_minimal_url || settings?.logo_url;
    return path ? buildAssetUrl(path) : null;
  }, [settings]);

  const getFaviconUrl = useCallback(() => {
    const path = settings?.favicon_url;
    return path ? buildAssetUrl(path) : null;
  }, [settings]);

  useEffect(() => {
    if (loading) return;
    applyBrowserFavicon(getFaviconUrl());
  }, [loading, getFaviconUrl]);

  const getFooterText = useCallback(() => {
    const name = settings?.nom || 'TREND';
    return `${name} ©${moment().year()} Powered by WanderTeck`;
  }, [settings]);

  const value = useMemo(
    () => ({
      settings,
      loading,
      refreshSettings,
      getLogoUrl,
      getMinimalLogoUrl,
      getFaviconUrl,
      getFooterText,
    }),
    [
      settings,
      loading,
      refreshSettings,
      getLogoUrl,
      getMinimalLogoUrl,
      getFaviconUrl,
      getFooterText,
    ]
  );

  // Brand tokens reach the UI through the CSS variables applied above, so there
  // is no theme provider left to wrap the tree in.
  return (
    <EntrepriseSettingsContext.Provider value={value}>
      {children}
    </EntrepriseSettingsContext.Provider>
  );
};

export const useEntrepriseSettings = () => useContext(EntrepriseSettingsContext);

export default EntrepriseSettingsContext;
